// ============================================================================
// PairsForm - EmailComposer (dialogue universel de composition d'email)
// ============================================================================
// Utilisé avant chaque envoi/programmation d'email pour permettre à
// l'utilisateur d'éditer le modèle, choisir le compte, ajouter CC/PJ,
// insérer des variables et prévisualiser avant envoi.
// ============================================================================

import { useState, useEffect, useRef } from "react";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Tabs,
  Tab,
  IconButton,
  Tooltip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  RadioGroup,
  Radio,
  FormControlLabel,
  Checkbox,
  CircularProgress,
  Alert,
  Divider,
  Collapse,
  } from "@mui/material";
import {
  Mail,
  Send,
  Eye,
  Edit,
  Clock,
  Users,
  Plus,
  X,
  Paperclip,
  Variable,
  AtSign,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { api } from "../lib/api";
import { useIsMobile } from "../hooks/useIsMobile";
import { DialogMobile } from "./ui/DialogMobile";
import ChampDate from "./ChampDate";

// ============================================================================
// Types
// ============================================================================

interface MergeField {
  key: string;
  label: string;
  category: string;
  example: string;
}

interface CompteEmail {
  id: string;
  nom: string;
  emailDefaut: string;
  nomExpediteur: string;
  estDefaut: boolean;
  actif: boolean;
}

interface Attachment {
  filename: string;
  content: string; // base64
  size: number;
}

export interface Destinataire {
  id: string;
  nom: string;
  prenom: string;
  email: string;
}

export interface EmailComposerResult {
  sujet: string;
  corpsHtml: string;
  compteEmailId?: string;
  cc: string[];
  attachments: Attachment[];
  dateEnvoi?: string; // ISO date for scheduling, undefined for immediate
  selectedDestinataires?: string[]; // IDs des destinataires sélectionnés
}

interface EmailComposerProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (config: EmailComposerResult) => void;

  // Valeurs initiales
  defaultSujet: string;
  defaultCorps: string; // HTML template avec {{variables}}

  // Contexte pour les champs de merge
  contexte: "questionnaire" | "sondage" | "communication" | "rappel" | "seance";

  // Info destinataires — ancien mode (juste un nombre)
  nbDestinataires?: number;

  // Destinataires sélectionnables (nouveau mode avec checkboxes)
  destinataires?: Destinataire[];

  // Programmation
  allowScheduling?: boolean;
  defaultImmediate?: boolean; // true = envoi immédiat par défaut

  // Label du bouton de confirmation
  confirmLabel?: string;
}

// ============================================================================
// Composant principal
// ============================================================================

export default function EmailComposer({
  open,
  onClose,
  onConfirm,
  defaultSujet,
  defaultCorps,
  contexte,
  nbDestinataires,
  destinataires,
  allowScheduling = true,
  defaultImmediate = true,
  confirmLabel,
}: EmailComposerProps) {
  const isMobile = useIsMobile();

  // Onglet actif : édition ou prévisualisation
  const [activeTab, setActiveTab] = useState(0);

  // Champs du formulaire
  const [sujet, setSujet] = useState(defaultSujet);
  const [corpsHtml, setCorpsHtml] = useState(defaultCorps);
  const [compteEmailId, setCompteEmailId] = useState("");
  const [cc, setCc] = useState("");
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [envoyerMode, setEnvoyerMode] = useState<"immediat" | "programme">(
    defaultImmediate ? "immediat" : "programme"
  );
  const [dateEnvoi, setDateEnvoi] = useState("");
  const [heureEnvoi, setHeureEnvoi] = useState("09:00");

  // Sélection des destinataires
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [destExpanded, setDestExpanded] = useState(false);

  // Données chargées
  const [comptes, setComptes] = useState<CompteEmail[]>([]);
  const [mergeFields, setMergeFields] = useState<MergeField[]>([]);
  const [loadingComptes, setLoadingComptes] = useState(false);

  // Preview
  const [previewHtml, setPreviewHtml] = useState("");
  const [previewSujet, setPreviewSujet] = useState("");
  const [loadingPreview, setLoadingPreview] = useState(false);

  // Ref pour le textarea du corps
  const corpsRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // ============================================================================
  // Charger les comptes email et les champs de merge à l'ouverture
  // ============================================================================

  useEffect(() => {
    if (!open) return;

    // Reset
    setSujet(defaultSujet);
    setCorpsHtml(defaultCorps);
    setActiveTab(0);
    setCc("");
    setAttachments([]);
    setEnvoyerMode(defaultImmediate ? "immediat" : "programme");
    setDateEnvoi("");
    setHeureEnvoi("09:00");
    setDestExpanded(false);
    // Sélectionner tous les destinataires par défaut
    if (destinataires) {
      setSelectedIds(new Set(destinataires.map((d) => d.id)));
    }

    // Charger les comptes email
    setLoadingComptes(true);
    api
      .get("/comptes-email")
      .then((res) => {
        const comptesData = (res.data.comptes || []).filter(
          (c: CompteEmail) => c.actif
        );
        setComptes(comptesData);
        // Sélectionner le compte par défaut
        const defaut = comptesData.find((c: CompteEmail) => c.estDefaut);
        if (defaut) setCompteEmailId(defaut.id);
        else if (comptesData.length > 0) setCompteEmailId(comptesData[0].id);
      })
      .catch(() => {
        setComptes([]);
      })
      .finally(() => setLoadingComptes(false));

    // Charger les champs de merge
    api
      .get("/email/merge-fields", { params: { contexte } })
      .then((res) => setMergeFields(res.data.fields || []))
      .catch(() => setMergeFields([]));
  }, [open, defaultSujet, defaultCorps, contexte, defaultImmediate]);

  // ============================================================================
  // Charger la prévisualisation quand on bascule sur l'onglet preview
  // ============================================================================

  useEffect(() => {
    if (activeTab !== 1 || !open) return;

    setLoadingPreview(true);
    api
      .post("/email/preview", { sujet, corpsHtml })
      .then((res) => {
        setPreviewSujet(res.data.sujet || sujet);
        setPreviewHtml(res.data.corpsHtml || corpsHtml);
      })
      .catch(() => {
        setPreviewSujet(sujet);
        setPreviewHtml(corpsHtml);
      })
      .finally(() => setLoadingPreview(false));
  }, [activeTab, open]);

  // ============================================================================
  // Insérer une variable dans le corps
  // ============================================================================

  const insertVariable = (key: string) => {
    const tag = `{{${key}}}`;
    const textarea = corpsRef.current;

    if (textarea) {
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const before = corpsHtml.substring(0, start);
      const after = corpsHtml.substring(end);
      setCorpsHtml(before + tag + after);
      // Remettre le focus après insertion
      setTimeout(() => {
        textarea.focus();
        textarea.setSelectionRange(start + tag.length, start + tag.length);
      }, 50);
    } else {
      // Ajouter à la fin
      setCorpsHtml(corpsHtml + tag);
    }
  };

  // ============================================================================
  // Gestion des pièces jointes
  // ============================================================================

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;

    for (let i = 0; i < files.length; i++) {
      const file = files[i];

      // Limite 5 Mo par fichier
      if (file.size > 5 * 1024 * 1024) {
        continue;
      }

      const reader = new FileReader();
      reader.onload = () => {
        const base64 = (reader.result as string).split(",")[1];
        setAttachments((prev) => [
          ...prev,
          { filename: file.name, content: base64, size: file.size },
        ]);
      };
      reader.readAsDataURL(file);
    }

    // Reset input
    e.target.value = "";
  };

  const removeAttachment = (index: number) => {
    setAttachments((prev) => prev.filter((_, i) => i !== index));
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} o`;
    if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} Ko`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} Mo`;
  };

  // ============================================================================
  // Confirmation
  // ============================================================================

  // Nombre effectif de destinataires
  const effectiveNbDest = destinataires ? selectedIds.size : (nbDestinataires || 0);

  const handleConfirm = () => {
    const ccList = cc
      .split(/[,;\s]+/)
      .map((e) => e.trim())
      .filter((e) => e.includes("@"));

    let scheduledDate: string | undefined;
    if (envoyerMode === "programme" && dateEnvoi) {
      scheduledDate = new Date(`${dateEnvoi}T${heureEnvoi}:00`).toISOString();
    }

    onConfirm({
      sujet,
      corpsHtml,
      compteEmailId: compteEmailId || undefined,
      cc: ccList,
      attachments,
      dateEnvoi: scheduledDate,
      selectedDestinataires: destinataires ? Array.from(selectedIds) : undefined,
    });
  };

  // ============================================================================
  // Grouper les merge fields par catégorie
  // ============================================================================

  const fieldsByCategory = mergeFields.reduce(
    (acc, field) => {
      if (!acc[field.category]) acc[field.category] = [];
      acc[field.category].push(field);
      return acc;
    },
    {} as Record<string, MergeField[]>
  );

  // ============================================================================
  // Render
  // ============================================================================

  return (
    <DialogMobile
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          pb: 1,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Mail size={20} color="#E1512C" />
          <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: "1rem", md: "1.15rem" } }}>
            Composer l'email
          </Typography>
        </Box>
        <Chip
          icon={<Users size={14} />}
          label={`${effectiveNbDest} destinataire${effectiveNbDest > 1 ? "s" : ""}`}
          size="small"
          sx={{ fontSize: "0.75rem" }}
        />
      </DialogTitle>

      <Box sx={{ px: { xs: 2, md: 3 }, borderBottom: 1, borderColor: "divider" }}>
        <Tabs
          value={activeTab}
          onChange={(_, val) => setActiveTab(val)}
          sx={{ "& .MuiTab-root": { textTransform: "none", fontWeight: 500, minHeight: 42 } }}
        >
          <Tab
            icon={<Edit size={16} />}
            iconPosition="start"
            label="Édition"
            sx={{ gap: 0.5 }}
          />
          <Tab
            icon={<Eye size={16} />}
            iconPosition="start"
            label="Prévisualisation"
            sx={{ gap: 0.5 }}
          />
        </Tabs>
      </Box>

      <DialogContent sx={{ p: { xs: 2, md: 3 } }}>
        {/* ================================================================ */}
        {/* Onglet Édition */}
        {/* ================================================================ */}
        {activeTab === 0 && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
            {/* Destinataires */}
            {destinataires && destinataires.length > 0 && (
              <Box>
                <Box
                  onClick={() => setDestExpanded(!destExpanded)}
                  sx={{
                    display: "flex", alignItems: "center", gap: 1, cursor: "pointer",
                    p: 1.5, borderRadius: 1.5, border: "1px solid", borderColor: "divider",
                    "&:hover": { bgcolor: "rgba(75, 183, 165, 0.04)" },
                  }}
                >
                  <Users size={16} color="#4BB7A5" />
                  <Typography variant="body2" sx={{ flex: 1, fontWeight: 500 }}>
                    {selectedIds.size} destinataire{selectedIds.size > 1 ? "s" : ""} sur {destinataires.length}
                  </Typography>
                  <Chip
                    label={selectedIds.size === destinataires.length ? "Tous sélectionnés" : `${destinataires.length - selectedIds.size} retiré(s)`}
                    size="small"
                    sx={{
                      fontSize: "0.7rem", height: 22,
                      bgcolor: selectedIds.size === destinataires.length ? "rgba(75, 183, 165, 0.1)" : "rgba(245, 166, 35, 0.1)",
                      color: selectedIds.size === destinataires.length ? "#3A9A8A" : "#E09520",
                    }}
                  />
                  {destExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                </Box>
                <Collapse in={destExpanded}>
                  <Box sx={{ border: "1px solid", borderColor: "divider", borderTop: 0, borderRadius: "0 0 8px 8px", maxHeight: 240, overflow: "auto" }}>
                    {/* Tout sélectionner / désélectionner */}
                    <Box sx={{ px: 1.5, py: 0.5, borderBottom: "1px solid", borderColor: "divider", bgcolor: "rgba(0,0,0,0.02)" }}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            size="small"
                            checked={selectedIds.size === destinataires.length}
                            indeterminate={selectedIds.size > 0 && selectedIds.size < destinataires.length}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedIds(new Set(destinataires.map((d) => d.id)));
                              } else {
                                setSelectedIds(new Set());
                              }
                            }}
                            sx={{ py: 0.25, "& .MuiSvgIcon-root": { fontSize: 18 } }}
                          />
                        }
                        label={<Typography variant="caption" sx={{ fontWeight: 600 }}>Tout sélectionner</Typography>}
                      />
                    </Box>
                    {destinataires.map((d) => (
                      <Box
                        key={d.id}
                        sx={{
                          px: 1.5, py: 0.25,
                          "&:hover": { bgcolor: "rgba(0,0,0,0.02)" },
                          opacity: selectedIds.has(d.id) ? 1 : 0.5,
                        }}
                      >
                        <FormControlLabel
                          control={
                            <Checkbox
                              size="small"
                              checked={selectedIds.has(d.id)}
                              onChange={(e) => {
                                setSelectedIds((prev) => {
                                  const next = new Set(prev);
                                  if (e.target.checked) next.add(d.id);
                                  else next.delete(d.id);
                                  return next;
                                });
                              }}
                              sx={{ py: 0.25, "& .MuiSvgIcon-root": { fontSize: 18 } }}
                            />
                          }
                          label={
                            <Box sx={{ display: "flex", gap: 1, alignItems: "baseline" }}>
                              <Typography variant="body2" sx={{ fontSize: "0.8rem" }}>
                                {d.prenom} {d.nom}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {d.email}
                              </Typography>
                            </Box>
                          }
                        />
                      </Box>
                    ))}
                  </Box>
                </Collapse>
              </Box>
            )}

            {/* Compte d'envoi */}
            {comptes.length > 0 && (
              <FormControl size="small" fullWidth>
                <InputLabel>Compte d'envoi</InputLabel>
                <Select
                  value={compteEmailId}
                  label="Compte d'envoi"
                  onChange={(e) => setCompteEmailId(e.target.value)}
                  startAdornment={<AtSign size={14} style={{ marginRight: 8, color: "#9CA3AF" }} />}
                >
                  {comptes.map((c) => (
                    <MenuItem key={c.id} value={c.id}>
                      <Box>
                        <Typography variant="body2">{c.nom}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {c.nomExpediteur} &lt;{c.emailDefaut}&gt;
                        </Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            {comptes.length === 0 && !loadingComptes && (
              <Alert severity="info" sx={{ py: 0.5 }}>
                Aucun compte email configuré. L'envoi utilisera le compte par défaut du système.
              </Alert>
            )}

            {/* Objet */}
            <TextField
              label="Objet de l'email"
              fullWidth
              value={sujet}
              onChange={(e) => setSujet(e.target.value)}
              size="small"
            />

            {/* Corps */}
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: "block", fontWeight: 500 }}>
                Corps de l'email (HTML supporté — utilisez {"{{variable}}"} pour les champs dynamiques)
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={isMobile ? 8 : 12}
                value={corpsHtml}
                onChange={(e) => setCorpsHtml(e.target.value)}
                inputRef={corpsRef}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    fontFamily: "monospace",
                    fontSize: "0.85rem",
                    lineHeight: 1.5,
                  },
                }}
              />
            </Box>

            {/* Variables de merge */}
            <Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 1 }}>
                <Variable size={14} color="#4BB7A5" />
                <Typography variant="caption" sx={{ fontWeight: 600, color: "text.secondary" }}>
                  Champs disponibles (cliquez pour insérer)
                </Typography>
              </Box>
              {Object.entries(fieldsByCategory).map(([category, fields]) => (
                <Box key={category} sx={{ mb: 1 }}>
                  <Typography
                    variant="caption"
                    color="text.disabled"
                    sx={{ fontSize: "0.65rem", textTransform: "uppercase", letterSpacing: 0.5 }}
                  >
                    {category}
                  </Typography>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 0.25 }}>
                    {fields.map((field) => (
                      <Chip
                        key={field.key}
                        label={`{{${field.key}}}`}
                        size="small"
                        onClick={() => insertVariable(field.key)}
                        sx={{
                          cursor: "pointer",
                          fontSize: "0.7rem",
                          height: 24,
                          bgcolor: "rgba(75, 183, 165, 0.08)",
                          borderColor: "rgba(75, 183, 165, 0.3)",
                          "&:hover": {
                            bgcolor: "rgba(75, 183, 165, 0.15)",
                            borderColor: "#4BB7A5",
                          },
                        }}
                        variant="outlined"
                      />
                    ))}
                  </Box>
                </Box>
              ))}
            </Box>

            <Divider />

            {/* CC */}
            <TextField
              label="CC (destinataires en copie)"
              fullWidth
              size="small"
              value={cc}
              onChange={(e) => setCc(e.target.value)}
              placeholder="email1@exemple.fr, email2@exemple.fr"
              helperText="Séparez les adresses par des virgules"
            />

            {/* Pièces jointes */}
            <Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                <Paperclip size={14} color="#9CA3AF" />
                <Typography variant="caption" sx={{ fontWeight: 600, color: "text.secondary" }}>
                  Pièces jointes
                </Typography>
                <Button
                  size="small"
                  startIcon={<Plus size={14} />}
                  onClick={() => fileInputRef.current?.click()}
                  sx={{ textTransform: "none", fontSize: "0.75rem" }}
                >
                  Ajouter
                </Button>
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  hidden
                  onChange={handleFileChange}
                />
              </Box>
              {attachments.length === 0 ? (
                <Typography variant="caption" color="text.disabled">
                  Aucune pièce jointe (max 5 Mo par fichier)
                </Typography>
              ) : (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                  {attachments.map((att, i) => (
                    <Chip
                      key={i}
                      label={`${att.filename} (${formatFileSize(att.size)})`}
                      size="small"
                      onDelete={() => removeAttachment(i)}
                      sx={{ fontSize: "0.7rem" }}
                    />
                  ))}
                </Box>
              )}
            </Box>

            <Divider />

            {/* Programmation */}
            {allowScheduling && (
              <Box>
                <Typography variant="caption" sx={{ fontWeight: 600, color: "text.secondary", mb: 1, display: "block" }}>
                  <Clock size={14} style={{ verticalAlign: "middle", marginRight: 4 }} />
                  Programmation de l'envoi
                </Typography>
                <RadioGroup
                  value={envoyerMode}
                  onChange={(e) => setEnvoyerMode(e.target.value as "immediat" | "programme")}
                >
                  <FormControlLabel
                    value="immediat"
                    control={<Radio size="small" />}
                    label={
                      <Typography variant="body2">Envoyer immédiatement</Typography>
                    }
                  />
                  <FormControlLabel
                    value="programme"
                    control={<Radio size="small" />}
                    label={
                      <Typography variant="body2">Programmer l'envoi</Typography>
                    }
                  />
                </RadioGroup>
                {envoyerMode === "programme" && (
                  <Box sx={{ display: "flex", gap: 2, mt: 1, ml: 4 }}>
                    <ChampDate fullWidth={false}
                      size="small"
                      label="Date"
                      value={dateEnvoi}
                      onChange={(v) => setDateEnvoi(v)}
                      sx={{ width: 180 }}
                    />
                    <TextField
                      type="time"
                      size="small"
                      label="Heure"
                      value={heureEnvoi}
                      onChange={(e) => setHeureEnvoi(e.target.value)}
                      slotProps={{ inputLabel: { shrink: true } }}
                      sx={{ width: 130 }}
                    />
                  </Box>
                )}
              </Box>
            )}
          </Box>
        )}

        {/* ================================================================ */}
        {/* Onglet Prévisualisation */}
        {/* ================================================================ */}
        {activeTab === 1 && (
          <Box>
            {loadingPreview ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                <CircularProgress size={24} sx={{ color: "#4BB7A5" }} />
              </Box>
            ) : (
              <>
                <Alert severity="info" sx={{ mb: 2, py: 0.5 }}>
                  Aperçu avec des données d'exemple. Les variables seront remplacées par les vraies valeurs de chaque destinataire.
                </Alert>

                {/* Subject preview */}
                <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                    Objet
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {previewSujet || sujet}
                  </Typography>
                </Paper>

                {/* Body preview */}
                <Paper
                  variant="outlined"
                  sx={{ p: 0, overflow: "hidden" }}
                >
                  <Box
                    sx={{
                      p: 2,
                      maxHeight: isMobile ? 400 : 500,
                      overflowY: "auto",
                      bgcolor: "#fafafa",
                    }}
                    dangerouslySetInnerHTML={{
                      __html: previewHtml || corpsHtml,
                    }}
                  />
                </Paper>

                {/* Attachments preview */}
                {attachments.length > 0 && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                      Pièces jointes ({attachments.length})
                    </Typography>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 0.5 }}>
                      {attachments.map((att, i) => (
                        <Chip
                          key={i}
                          icon={<Paperclip size={12} />}
                          label={`${att.filename} (${formatFileSize(att.size)})`}
                          size="small"
                          sx={{ fontSize: "0.7rem" }}
                        />
                      ))}
                    </Box>
                  </Box>
                )}

                {/* CC preview */}
                {cc.trim() && (
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                      CC : {cc}
                    </Typography>
                  </Box>
                )}
              </>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: { xs: 2, md: 3 }, py: 2, gap: 1, borderTop: 1, borderColor: "divider" }}>
        <Button onClick={onClose} sx={{ textTransform: "none" }}>
          Annuler
        </Button>
        <Button
          variant="contained"
          onClick={handleConfirm}
          disabled={!sujet.trim() || effectiveNbDest === 0}
          startIcon={
            envoyerMode === "programme" ? (
              <Clock size={16} />
            ) : (
              <Send size={16} />
            )
          }
          sx={{
            bgcolor: "#4BB7A5",
            "&:hover": { bgcolor: "#3a9e8e" },
            textTransform: "none",
          }}
        >
          {confirmLabel
            ? confirmLabel
            : envoyerMode === "programme"
              ? "Programmer l'envoi"
              : `Envoyer à ${effectiveNbDest} destinataire${effectiveNbDest > 1 ? "s" : ""}`}
        </Button>
      </DialogActions>
    </DialogMobile>
  );
}
